/**
 * PIPCARE-1358: every field the Patients table can filter and sort on, in one place.
 * The filter builder, the active-filter chips and the column headers all read from this list,
 * so adding a field is one entry here (plus the backend filterable_by / sortable_by).
 */
/** `list`: choices come from a Settings list (hospitals, surgeons, outreach assignees, surgery types). */
/** `time`: a wall-clock "HH:MM" (24-hour) value, e.g. surgery time. */
export type PatientFieldType = "text" | "date" | "time" | "enum" | "number" | "practitioner" | "list";

export interface PatientFieldOption {
  value: string;
  label: string;
}

export interface PatientTableField {
  key: string;
  label: string;
  type: PatientFieldType;
  group: "Main" | "Referral" | "Contact" | "Health" | "Outcomes";
  options?: PatientFieldOption[];
  /** Overrides the type's default conditions (e.g. list fields that hold several values). */
  operators?: PatientFieldOption[];
}

/** For fields holding several values (comorbidities, clearances): eq/not_eq mean "has this one". */
const INCLUDES_EQ: PatientFieldOption[] = [
  { value: "eq", label: "includes" },
  { value: "not_eq", label: "doesn't include" },
];
/** Tags only support in/not_in on the backend. */
const INCLUDES_IN: PatientFieldOption[] = [
  { value: "in", label: "includes" },
  { value: "not_in", label: "doesn't include" },
];

export const RISK_OPTIONS: PatientFieldOption[] = [
  { value: "high", label: "High risk" },
  { value: "medium", label: "Medium risk" },
  { value: "low", label: "Low risk" },
  { value: "no_surgery_date", label: "No surgery date" },
  { value: "surgery_past", label: "Surgery past" },
];

export const ACTIVATION_OPTIONS: PatientFieldOption[] = [
  { value: "pending", label: "Pending (incl. 3+ days)" },
  { value: "overdue", label: "Not activated 3+ days" },
  { value: "activated", label: "Activated" },
];

const YES_NO: PatientFieldOption[] = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

export const PATIENT_TABLE_FIELDS: PatientTableField[] = [
  { key: "first_name", label: "First name", type: "text", group: "Main" },
  { key: "last_name", label: "Last name", type: "text", group: "Main" },
  { key: "primary_phone_number", label: "Phone", type: "text", group: "Main" },
  { key: "enrolled_date", label: "Activation date", type: "date", group: "Main" },
  { key: "activation_status", label: "Activation status", type: "enum", group: "Main", options: ACTIVATION_OPTIONS },
  { key: "surgery_date", label: "Surgery date", type: "date", group: "Main" },
  { key: "surgery_time", label: "Surgery time", type: "time", group: "Main" },
  { key: "surgery_type", label: "Surgery type", type: "list", group: "Main" },
  { key: "readiness", label: "Risk level", type: "enum", group: "Main", options: RISK_OPTIONS },
  { key: "hospital", label: "Hospital", type: "list", group: "Main" },
  { key: "outreach_assignee", label: "Outreach assignee", type: "list", group: "Main" },
  { key: "practitioner_id", label: "Practitioner", type: "practitioner", group: "Main" },
  { key: "surgery_status", label: "Surgery status", type: "text", group: "Referral" },
  { key: "patient_status", label: "Patient status", type: "text", group: "Referral" },
  { key: "program", label: "Program", type: "text", group: "Referral" },
  { key: "referral_date", label: "Referral date", type: "date", group: "Referral" },
  { key: "created_at", label: "Added to Capable", type: "date", group: "Referral" },
  { key: "surgeon", label: "Surgeon", type: "list", group: "Referral" },
  { key: "service_line", label: "Service line", type: "list", group: "Referral" },
  { key: "health_system", label: "Health system", type: "text", group: "Referral" },
  { key: "patient_type", label: "Patient type", type: "text", group: "Referral" },
  { key: "visit_type", label: "Visit type", type: "text", group: "Referral" },
  { key: "ip_op", label: "IP/OP", type: "text", group: "Referral" },
  { key: "source", label: "Source", type: "text", group: "Referral" },
  { key: "source_status", label: "Source status", type: "text", group: "Referral" },
  { key: "engagement_status", label: "Engagement status", type: "text", group: "Referral" },
  { key: "opt_status", label: "Opt status", type: "text", group: "Referral" },
  { key: "assigned_staff_member", label: "Assigned staff member", type: "text", group: "Referral" },
  { key: "pcp_physician", label: "PCP", type: "text", group: "Referral" },
  { key: "fin", label: "FIN", type: "text", group: "Referral" },
  { key: "cancellation_reason", label: "Cancellation reason", type: "text", group: "Referral" },
  { key: "nih_grant_patient", label: "NIH grant patient", type: "enum", group: "Referral", options: YES_NO },
  { key: "email", label: "Email", type: "text", group: "Contact" },
  { key: "patient_number", label: "Patient number", type: "text", group: "Contact" },
  { key: "epic_mrn", label: "MRN", type: "text", group: "Contact" },
  { key: "preferred_name", label: "Preferred name", type: "text", group: "Contact" },
  { key: "birth_date", label: "Birth date", type: "date", group: "Contact" },
  { key: "mobile_phone_number", label: "Mobile phone", type: "text", group: "Contact" },
  { key: "home_phone_number", label: "Home phone", type: "text", group: "Contact" },
  { key: "opt_in_sms", label: "Opted in to texts", type: "enum", group: "Contact", options: YES_NO },
  { key: "opt_in_email", label: "Opted in to email", type: "enum", group: "Contact", options: YES_NO },
  { key: "age", label: "Age", type: "number", group: "Health" },
  { key: "comorbidities", label: "Comorbidities", type: "list", group: "Health", operators: INCLUDES_EQ },
  { key: "clearances", label: "Clearances", type: "list", group: "Health", operators: INCLUDES_EQ },
  { key: "smoking", label: "Smoking", type: "enum", group: "Health", options: YES_NO },
  { key: "diabetes", label: "Diabetes", type: "enum", group: "Health", options: YES_NO },
  { key: "anemia", label: "Anemia", type: "enum", group: "Health", options: YES_NO },
  { key: "hypertension", label: "Hypertension", type: "enum", group: "Health", options: YES_NO },
  { key: "deceased", label: "Deceased", type: "enum", group: "Outcomes", options: YES_NO },
  { key: "length_of_stay", label: "Length of stay", type: "number", group: "Outcomes" },
  { key: "postoperative_ed_visit", label: "Post-op ED visit", type: "text", group: "Outcomes" },
  { key: "postoperative_ed_visit_date", label: "Post-op ED visit date", type: "date", group: "Outcomes" },
  { key: "postoperative_readmission", label: "Post-op readmission", type: "text", group: "Outcomes" },
  { key: "postoperative_readmission_date", label: "Post-op readmission date", type: "date", group: "Outcomes" },
  { key: "outcomes_collected", label: "Outcomes collected", type: "enum", group: "Outcomes", options: YES_NO },
  { key: "tags", label: "Tags", type: "list", group: "Main", operators: INCLUDES_IN },
];

export const FIELD_BY_KEY: Record<string, PatientTableField> = Object.fromEntries(
  PATIENT_TABLE_FIELDS.map((field) => [field.key, field])
);

/** Keys the backend can sort on (Api::PatientsController.sortable_by). */
export const SORTABLE_KEYS = [
  "first_name",
  "last_name",
  "enrolled_date",
  "surgery_date",
  "surgery_time",
  "surgery_type",
  "readiness",
  "hospital",
  "outreach_assignee",
  "surgery_status",
  "patient_status",
  "program",
  "referral_date",
  "created_at",
  "updated_at",
] as const;

/**
 * Newest referrals first, so a patient you just referred is at the top. Ties (same referral date)
 * go newest-created first; blank referral dates sort last.
 */
export const DEFAULT_SORT = ["-referral_date", "-created_at"];

export const OPERATORS_BY_TYPE: Record<PatientFieldType, PatientFieldOption[]> = {
  text: [
    { value: "matches", label: "contains" },
    { value: "eq", label: "is" },
    { value: "not_eq", label: "is not" },
  ],
  date: [
    { value: "eq", label: "is on" },
    { value: "gteq", label: "on or after" },
    { value: "lteq", label: "on or before" },
  ],
  time: [
    { value: "eq", label: "is at" },
    { value: "gteq", label: "at or after" },
    { value: "lteq", label: "at or before" },
  ],
  enum: [
    { value: "eq", label: "is" },
    { value: "not_eq", label: "is not" },
  ],
  number: [
    { value: "eq", label: "is" },
    { value: "gteq", label: "at least" },
    { value: "lteq", label: "at most" },
  ],
  practitioner: [{ value: "in", label: "is" }],
  list: [
    { value: "eq", label: "is" },
    { value: "not_eq", label: "is not" },
  ],
};

/** The conditions offered for a field. */
export const operatorsFor = (key: string): PatientFieldOption[] => {
  const field = FIELD_BY_KEY[key];
  return field?.operators ?? OPERATORS_BY_TYPE[field?.type ?? "text"];
};

export const operatorLabel = (operator: string, key?: string): string =>
  (key ? operatorsFor(key).find((option) => option.value === operator)?.label : undefined) ??
  Object.values(OPERATORS_BY_TYPE)
    .flat()
    .find((option) => option.value === operator)?.label ?? operator;
